import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
} from '@mui/material'
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Link as LinkIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material'
import { formatDate, formatDistanceToNow } from '../../utils/formatters'
import { useTransactionDetails } from '../../hooks/useTransactionDetails'

const TransactionDetailsDialog = ({ 
  open, 
  transaction, 
  onClose, 
  getTypeChip, 
  getStatusChip 
}) => {
  const { 
    data: transactionDetails, 
    isLoading, 
    error, 
    refetch 
  } = useTransactionDetails(transaction?.id, { enabled: open && !!transaction })

  useEffect(() => {
    if (open && transaction) {
      refetch()
    }
  }, [open, transaction, refetch])

  const getTransactionIcon = (type, amount) => {
    const iconProps = { sx: { fontSize: 40 } }
    
    switch (type) {
      case 'purchase':
        return <TrendingUpIcon color="success" {...iconProps} />
      case 'rental':
        return <TrendingDownIcon color="primary" {...iconProps} />
      case 'admin_adjustment':
        return amount > 0 ? 
          <TrendingUpIcon color="warning" {...iconProps} /> : 
          <TrendingDownIcon color="warning" {...iconProps} />
      default:
        return <ReceiptIcon color="action" {...iconProps} />
    }
  }

  const getUserAvatar = (email) => {
    if (!email) return null
    const initials = email.substring(0, 2).toUpperCase()
    return (
      <Avatar sx={{ width: 48, height: 48, fontSize: '1.2rem' }}>
        {initials}
      </Avatar>
    )
  }

  const getTransactionDescription = (type) => {
    switch (type) {
      case 'purchase':
        return 'Kullanıcı kredi satın aldı'
      case 'rental':
        return 'SMS numarası kiralandı'
      case 'admin_adjustment':
        return 'Admin tarafından kredi düzeltmesi yapıldı'
      default:
        return 'Bilinmeyen işlem tipi'
    }
  }

  if (!transaction) return null

  const details = transactionDetails?.transaction || transaction

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            İşlem Detayları
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">
            İşlem detayları yüklenirken hata oluştu: {error.message}
          </Alert>
        ) : (
          <>
            {/* Transaction Header */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {getTransactionIcon(details.type, details.amount_in_credits)}
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      {getTypeChip && getTypeChip(details.type)}
                      {getStatusChip && getStatusChip(details.status)}
                    </Box>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {getTransactionDescription(details.type)}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      İşlem ID: {details.id}
                    </Typography>
                    
                    {details.external_transaction_id && (
                      <Typography variant="body2" color="text.secondary">
                        Harici ID: {details.external_transaction_id}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ textAlign: 'right' }}>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700,
                        color: details.amount_in_credits > 0 ? 'success.main' : 'error.main'
                      }}
                    >
                      {details.amount_in_credits > 0 ? '+' : ''}{details.amount_in_credits}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      kredi
                    </Typography>
                    {details.amount_usd && (
                      <Typography variant="body2" color="text.secondary">
                        (${Math.abs(details.amount_usd)})
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* User Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Kullanıcı Bilgileri
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {getUserAvatar(details.user_email)}
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {details.user_email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Kullanıcı ID: {details.user_id}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Transaction Details Grid */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      İşlem Bilgileri
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon><ReceiptIcon /></ListItemIcon>
                        <ListItemText 
                          primary="İşlem Tipi" 
                          secondary={details.type}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon><InfoIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Durum" 
                          secondary={details.status}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon><AttachMoneyIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Kredi Miktarı" 
                          secondary={`${details.amount_in_credits} kredi`}
                        />
                      </ListItem>
                      
                      {details.amount_usd && (
                        <ListItem>
                          <ListItemIcon><AttachMoneyIcon /></ListItemIcon>
                          <ListItemText 
                            primary="USD Değeri" 
                            secondary={`$${Math.abs(details.amount_usd)}`}
                          />
                        </ListItem>
                      )}
                      
                      {details.external_transaction_id && (
                        <ListItem>
                          <ListItemIcon><LinkIcon /></ListItemIcon>
                          <ListItemText 
                            primary="Harici İşlem ID" 
                            secondary={details.external_transaction_id}
                          />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Zaman Bilgileri
                    </Typography>
                    
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CalendarIcon /></ListItemIcon>
                        <ListItemText 
                          primary="İşlem Tarihi" 
                          secondary={formatDate(details.created_at)}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon><CalendarIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Ne Kadar Önce" 
                          secondary={formatDistanceToNow(details.created_at)}
                        />
                      </ListItem>
                      
                      <ListItem>
                        <ListItemIcon><SecurityIcon /></ListItemIcon>
                        <ListItemText 
                          primary="İşlem Saati" 
                          secondary={formatDate(details.created_at, 'HH:mm:ss')}
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Information */}
            {(details.description || details.notes) && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Ek Bilgiler
                  </Typography>
                  
                  {details.description && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Açıklama:
                      </Typography>
                      <Typography variant="body2">
                        {details.description}
                      </Typography>
                    </Box>
                  )}
                  
                  {details.notes && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Notlar:
                      </Typography>
                      <Typography variant="body2">
                        {details.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Transaction Status History */}
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  İşlem Geçmişi
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: details.status === 'completed' ? 'success.main' : 'warning.main'
                  }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      İşlem {details.status === 'completed' ? 'tamamlandı' : details.status}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(details.created_at)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  )
}

export default TransactionDetailsDialog